import { React, useState, useEffect } from "react";
import {
  Button,
  Box,
  Paper,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Grid
} from "@mui/material";
import { ArrowForwardIosSharp } from "@mui/icons-material";
// import Grid from "@mui/material/Unstable_Grid2";
import {
  DeleteForever,
  ExpandMore,
  ExpandLess,
  Add,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { getEmptyModel } from "../helper.js";

Expandable.propTypes = {
  item: PropTypes.object.isRequired,
  value: PropTypes.any,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showErrorText: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  renderFields: PropTypes.func,
  handleRemoveValues: PropTypes.func,
};

export default function Expandable(props) {
  const {
    item,
    value,
    renderFields,
    disabled,
    onChange,
    handleRemoveValues,
    error,
  } = props;
  const [renderItems, setRenderItems] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [originalItems, setOriginalItems] = useState(null);

  useEffect(() => {
    if (value && value.length > 0) {
      let rowModel = [];
      let newItems = [...renderItems];
      let originalIndexes = [];
      value.forEach((row, index) => {
        originalIndexes.push(index);
        const newChildren = getIndexedChildren(index);
        newItems = [...newItems, { children: newChildren, index: index }];
        rowModel = [
          ...rowModel,
          ...Object.entries(row).map(([key, value]) => ({
            target: {
              name: item.name + "#" + key + "#" + index,
              value: value,
            },
          })),
        ];
        item.children = [].concat(
          ...newItems.map((newItem) => {
            return newItem.children;
          })
        );
      });
      if (!originalItems && !item.allow_deletion) {
        setOriginalItems(originalIndexes);
      }
      onChange(rowModel);
      setRenderItems(newItems);
    } else {
      item.children = [];
    }
  }, [value]);

  useEffect(() => {
    if (renderItems) {
      updateObjectInModel(renderItems);
    }
  }, [renderItems]);

  const canDelete = (index) => {
    let result = true;
    if (!item.allow_deletion && originalItems) {
      result = !originalItems.includes(index);
    }
    return result;
  };

  const handleExpandedChange = () => {
    setExpanded(!expanded);
  };

  const updateObjectInModel = (newValue) => {
    onChange({
      target: {
        name: item.name + "#temp",
        value: newValue.map((row) => row.children.map((item) => item.name)),
      },
    });
  };

  const moveRow = (objToMove, direction) => {
    const updatedRenderItems = [...renderItems];
    const currentIndex = updatedRenderItems.indexOf(objToMove);

    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= updatedRenderItems.length) return;

    updatedRenderItems.splice(currentIndex, 1);
    updatedRenderItems.splice(newIndex, 0, objToMove);
    setRenderItems(updatedRenderItems);
  };

  const removeRow = (row) => {
    const names = row.children
      .filter((childItem) => childItem.hasOwnProperty("name"))
      .map((childItem) => childItem.name);
    item.children = item.children.filter(
      (childItem) =>
        childItem.hasOwnProperty("name") && !names.includes(childItem.name)
    );
    handleRemoveValues(names);

    const updatedRenderItems = renderItems.filter(
      (renderItem) => renderItem.index !== row.index
    );
    setRenderItems(updatedRenderItems);
  };

  const addRenderItem = () => {
    const newIndex = getMaxIndex();
    const newChildren = getIndexedChildren(newIndex);
    const newItems = [
      ...renderItems,
      { children: newChildren, index: newIndex },
    ];

    const rowModel = Object.entries(getEmptyModel(newChildren)).map(
      ([key, value]) => ({
        target: {
          name: key,
          value: value,
        },
      })
    );

    onChange(rowModel);
    item.children = [].concat(
      ...newItems.map((newItem) => {
        return newItem.children;
      })
    );
    setRenderItems(newItems);
  };

  const getMaxIndex = () => {
    let maxIndex = 0;
    renderItems.forEach((item) => {
      if (item.index > maxIndex) maxIndex = item.index;
    });
    return maxIndex + 1;
  };

  function replaceName(obj, oldName, newName) {
    for (const key in obj) {
      if (typeof obj[key] === "string") {
        obj[key] = obj[key].replace(oldName, newName);
      } else if (typeof obj[key] === "object") {
        replaceName(obj[key], oldName, newName);
      }
    }
  }

  const getIndexedChildren = (index) => {
    const newRow = JSON.parse(JSON.stringify(item.rowControls));
    const final = newRow.map((rowItem) => {
      if (rowItem.hasOwnProperty("name")) {
        const oldName = rowItem.name;
        const newName = item.name + "#" + oldName + "#" + index;
        rowItem.name = newName;
        replaceName(newRow, "'" + oldName + "'", "'" + newName + "'");
        replaceName(newRow, '"' + oldName + '"', '"' + newName + '"');
        return rowItem;
      } else {
        return rowItem;
      }
    });
    return final;
  };

  const buttonSX = {
    margin: "2px",
    minWidth: "32px",
    width: "32px",
    height: "32px",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderStyle: "solid",
        borderWidth: error ? "2px" : "1px",
        borderColor: error ? "#FF000060" : "#E6E6E6",
        borderRadius: "4px",
      }}
    >
      <Accordion
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={(e) => handleExpandedChange()}
        sx={{
          borderRadius: "4px",
        }}
      >
        <AccordionSummary
          id="{item.name}-header"
          expandIcon={<ArrowForwardIosSharp sx={{ fontSize: "12px" }} />}
          sx={{
            backgroundColor: "#E6E6E6",
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(90deg)",
            },
            ":hover": {
              backgroundColor: "clickableHover",
              cursor: "pointer",
            },
          }}
        >
          <Typography
            variant="regular_text_14px_normal"
            sx={{
              marginLeft: "20px",
            }}
          >
            {item.label}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderItems &&
            renderItems.map((renderItem, index) => (
              <Box
                key={renderItem.index}
                sx={{
                  margin: "5px",
                  pl: "0px",
                  pr: "0px",
                  pb: "15px",
                  pt: "10px",
                  backgroundColor: "#FCFCFC",
                  boxShadow: "0px 1px 0px #C0C0C0",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ flex: 1, pl: "5px", pr: "5px" }}>
                  <Grid container="true" spacing={1}>
                    {renderFields(renderItem.children)}
                  </Grid>
                </Box>
                <Box
                  sx={{
                    height: "100%",
                    mt: "2px",
                    ml: "10px",
                    mr: "0px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Button
                    disabled={disabled || !canDelete(renderItem.index)}
                    color="warning"
                    sx={buttonSX}
                    onClick={(e) => removeRow(renderItem)}
                  >
                    <DeleteForever />
                  </Button>
                  <Button
                    disabled={disabled || index === 0}
                    color="primary"
                    sx={buttonSX}
                    onClick={(e) => moveRow(renderItem, "up")}
                  >
                    <ExpandLess />
                  </Button>
                  <Button
                    disabled={disabled || index === renderItems.length - 1}
                    color="primary"
                    sx={buttonSX}
                    onClick={(e) => moveRow(renderItem, "down")}
                  >
                    <ExpandMore />
                  </Button>
                </Box>
              </Box>
            ))}
          <Box
            sx={{
              padding: "8px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Button
              size="small"
              disabled={disabled}
              onClick={() => addRenderItem()}
            >
              <Add sx={{ mr: "10px" }} />
              {item.label_add || "neu"}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
